import React, { useMemo, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, ArrowUpRight, CheckCircle2, XCircle, FileText, PhoneCall, Car, Heart,
  ShieldAlert, BarChart3, Fingerprint
} from 'lucide-react';
import TopBar from "../components/TopBar";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import '../styles/Products.css';

const ALL_PRODUCTS = [
  { 
    id: "motor-private", 
    title: "Motor Private", 
    serif: "Protection in Motion.",
    desc: "Comprehensive protection for your mobile assets.", 
    icon: <Car size={40} />, 
    tier: "Tier 1 Portfolio",
    riskRating: "Moderate",
    longDesc: "A sophisticated blend of liability protection and asset recovery. Designed for the discerning vehicle owner who values seamless continuity.",
    features: ["Concierge Roadside Support", "Windscreen Indemnity", "Excess Protector"],
    exclusions: ["Mechanical Breakdown", "Wear and Tear", "Unlicensed Driving"],
    claimSteps: ["Digital Notification", "Damage Assessment", "Approval", "Settlement"]
  },
  { 
    id: "medical-cover", 
    title: "Medical Cover", 
    serif: "The Architecture of Health.",
    desc: "Premier health solutions for families and individuals.", 
    icon: <Heart size={40} />, 
    tier: "Essential Asset",
    riskRating: "Low",
    longDesc: "Beyond insurance, this is a health management ecosystem. Providing rapid access to the region's most prestigious medical facilities.",
    features: ["Inpatient Mastery", "Chronic Care Support", "International Referrals"],
    exclusions: ["Cosmetic Procedures", "Experimental Treatments", "Self-inflicted Harm"],
    claimSteps: ["Pre-Authorization", "Facility Admission", "Direct Billing", "Discharge"]
  }
];

const Products = () => {
  const { productId } = useParams();
  const { pathname } = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const product = useMemo(() => 
    ALL_PRODUCTS.find(p => p.id === productId), [productId]
  );

  const GridView = () => (
    <div className="elite-root">
      <section className="elite-hero">
        <div className="gallery-container">
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="elite-eyebrow">ShieldGuard Solutions</span>
            <h1 className="elite-display-title">
              Engineered <span className="serif-italic">Reliability.</span>
            </h1>
          </motion.div>
        </div>
      </section>

      <main className="gallery-container">
        <div className="elite-masonry">
          {ALL_PRODUCTS.map((p, i) => (
            <motion.div 
              key={p.id} 
              className="elite-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <div className="card-inner">
                <div className="card-head">
                  <div className="elite-icon-wrap">{p.icon}</div>
                  <span className="risk-tag">{p.riskRating} Risk</span>
                </div>
                <h2 className="card-title">{p.title}</h2>
                <p className="card-text">{p.desc}</p>
                <Link to={`/products/${p.id}`} className="elite-cta-link">
                  View Specifications <ArrowUpRight size={18} />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );

  const DetailView = () => (
    <div className="elite-root">
      <header className="detail-header-v2">
        <div className="gallery-container">
          <Link to="/products" className="elite-back">
            <ArrowLeft size={16} /> Portfolio Index
          </Link>
          <div className="detail-hero-content">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <span className="tier-badge">{product.tier}</span>
              <h1 className="detail-huge-title">{product.title}</h1>
              <p className="serif-sub-title">{product.serif}</p>
            </motion.div>
            
            <div className="detail-quick-stats">
              <div className="stat-item">
                <ShieldAlert size={20} />
                <span>{product.riskRating} Risk Level</span>
              </div>
              <div className="stat-item">
                <Fingerprint size={20} />
                <span>Unique ID: {product.id.toUpperCase()}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="gallery-container detail-main-grid">
        <div className="left-stack">
          <section className="tech-spec-section">
            <h3 className="section-label">01. Strategy & Scope</h3>
            <p className="big-intro">{product.longDesc}</p>
            
            <div className="dual-feature-grid">
              <div className="feature-block inclusion-bg">
                <h4><CheckCircle2 size={20} /> Inclusions</h4>
                {product.features.map((f, i) => <div key={i} className="list-item"><span>—</span> {f}</div>)}
              </div>
              <div className="feature-block exclusion-bg">
                <h4><XCircle size={20} /> Exclusions</h4>
                {product.exclusions.map((e, i) => <div key={i} className="list-item"><span>—</span> {e}</div>)}
              </div>
            </div>
          </section>

          <section className="process-section">
            <h3 className="section-label">02. Claim Lifecycle</h3>
            <div className="process-line">
              {product.claimSteps.map((step, i) => (
                <div key={i} className="process-node">
                  <div className="node-circle">{i + 1}</div>
                  <span className="node-text">{step}</span>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="elite-sidebar">
          <div className="conversion-card">
            <h3>Initiate Engagement</h3>
            <p>Request a formal quotation for this portfolio.</p>
            <Link to="/quote" className="elite-btn-primary">Generate Quote</Link>
            <div className="sidebar-footer">
              <PhoneCall size={14} /> Support: 0748 660944 (Vansh)
            </div>
          </div>
          
          <div className="document-list">
            <div className="doc-item">
              <FileText size={18} />
              <span>Full Policy Wording</span>
            </div>
            <div className="doc-item">
              <BarChart3 size={18} />
              <span>Benefit Schedule</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );

  return (
    <div className="app-wrapper">
      <TopBar />
      <Navbar />
      <AnimatePresence mode="wait">
        {productId ? <DetailView key="detail" /> : <GridView key="grid" />}
      </AnimatePresence>
      <Footer />
    </div>
  );
};

export default Products;